import React, { useEffect, useState } from "react";
import {
  LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import api from "../lib/api";
import { useLiveMetrics } from "../hooks/useLiveMetrics";

/* ─── types ───────────────────────────────────────────────── */
interface Meter {
  id: number;
  serial_number: string;
  unit_number: string;
  property_name: string;
  status: "online" | "offline" | "fault";
  valve_open: boolean;
  last_seen: string | null;
  latest_reading: {
    cumulative_value: string;
    flow_rate: string;
    pressure_bar: string;
    battery_percent: number;
  } | null;
}

/* ─── helpers ─────────────────────────────────────────────── */
const STATUS_META = {
  online:  { label: "Online",  bg: "bg-emerald-50",  text: "text-emerald-600", dot: "bg-emerald-400", icon: "bg-emerald-100" },
  offline: { label: "Offline", bg: "bg-gray-100",    text: "text-gray-500",    dot: "bg-gray-400",    icon: "bg-gray-100" },
  fault:   { label: "Fault",   bg: "bg-red-50",      text: "text-red-500",     dot: "bg-red-400",     icon: "bg-red-100" },
};

function Skeleton({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse bg-gray-100 rounded-xl ${className}`} />;
}

/* ─── chart tooltip ──────────────────────────────────────── */
function MiniTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0f2557] border border-white/10 rounded-lg px-2.5 py-1.5 text-[11px] shadow-lg">
      <p className="text-blue-300/70">{label}</p>
      <p className="text-white font-semibold">{Number(payload[0].value).toFixed(3)}</p>
    </div>
  );
}

/* ─── battery bar ────────────────────────────────────────── */
function Battery({ pct }: { pct: number }) {
  const color = pct >= 60 ? "bg-emerald-400" : pct >= 30 ? "bg-amber-400" : "bg-red-400";
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-10 bg-gray-100 rounded-sm h-2 flex-shrink-0">
        <div className={`h-2 rounded-sm ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[11px] text-gray-500 font-medium">{pct}%</span>
    </div>
  );
}

/* ─── meter card ─────────────────────────────────────────── */
function MeterCard({ meter }: { meter: Meter }) {
  const { reading, connected } = useLiveMetrics(meter.id);
  const [chartData, setChartData] = useState<any[]>([]);
  const [showChart, setShowChart] = useState(false);

  useEffect(() => {
    api.get(`/meters/${meter.id}/readings/`).then(({ data }) => {
      setChartData(
        [...data].reverse().map((r: any) => ({
          time: new Date(r.timestamp).toLocaleTimeString("en-KE", { hour: "2-digit", minute: "2-digit" }),
          flow: parseFloat(r.flow_rate),
          pressure: parseFloat(r.pressure_bar),
        }))
      );
    }).catch(() => {});
  }, [meter.id]);

  const live: typeof meter.latest_reading = reading
    ? {
        cumulative_value: reading.cumulative,
        flow_rate: reading.flow_rate,
        pressure_bar: reading.pressure,
        battery_percent: 100,
      }
    : meter.latest_reading;

  const meta = STATUS_META[meter.status] ?? STATUS_META.offline;
  const battery = live?.battery_percent ?? 85;
  const lastSeenText = meter.last_seen
    ? new Date(meter.last_seen).toLocaleTimeString("en-KE", { timeStyle: "short" })
    : "—";

  return (
    <div className={`bg-white rounded-2xl border shadow-sm overflow-hidden transition-all ${meter.status === "online" ? "border-sky-100" : meter.status === "fault" ? "border-red-100" : "border-gray-100"}`}>

      {/* status stripe */}
      <div className={`h-1 ${meter.status === "online" ? "bg-gradient-to-r from-emerald-400 to-emerald-500" : meter.status === "fault" ? "bg-gradient-to-r from-red-400 to-red-500" : "bg-gray-200"}`} />

      <div className="p-5">
        {/* header */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${meta.icon}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke={meter.status === "online" ? "#10b981" : meter.status === "fault" ? "#ef4444" : "#9ca3af"} strokeWidth={1.8} className="w-5 h-5">
                <circle cx="12" cy="12" r="9"/>
                <path d="M12 7v5l3 3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="min-w-0">
              <p className="text-[14px] font-bold text-gray-800 truncate">{meter.property_name}</p>
              <p className="text-[12px] text-gray-400">Unit {meter.unit_number} · <span className="font-mono">{meter.serial_number}</span></p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
            <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full ${meta.bg} ${meta.text}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${meta.dot} ${meter.status === "online" ? "animate-pulse" : ""}`} />
              {meta.label}
            </span>
            {connected && (
              <span className="text-[10.5px] text-emerald-600 font-semibold flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-ping" />
                Live
              </span>
            )}
          </div>
        </div>

        {/* readings grid */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          {[
            {
              label: "Flow Rate",
              value: live ? `${parseFloat(live.flow_rate as string).toFixed(2)}` : "—",
              unit: "L/min",
              bg: "bg-sky-50",
              text: "text-sky-700",
              sub: "text-sky-500",
            },
            {
              label: "Pressure",
              value: live ? `${parseFloat(live.pressure_bar as string).toFixed(2)}` : "—",
              unit: "bar",
              bg: "bg-violet-50",
              text: "text-violet-700",
              sub: "text-violet-500",
            },
            {
              label: "Cumulative",
              value: live ? `${parseFloat(live.cumulative_value as string).toFixed(1)}` : "—",
              unit: "L",
              bg: "bg-emerald-50",
              text: "text-emerald-700",
              sub: "text-emerald-500",
            },
          ].map((stat) => (
            <div key={stat.label} className={`rounded-xl p-2.5 text-center ${stat.bg}`}>
              <p className={`text-[16px] font-bold ${stat.text} leading-tight`}>{stat.value}</p>
              <p className={`text-[10px] font-medium ${stat.sub}`}>{stat.unit}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* battery + valve + last seen */}
        <div className="flex items-center justify-between mb-3">
          <Battery pct={battery} />
          <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full ${meter.valve_open ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}>
            {meter.valve_open ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5">
                <path d="M12 2C8 7 5 10.5 5 14a7 7 0 0 0 14 0c0-3.5-3-7-7-12z" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5">
                <circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" strokeLinecap="round"/>
              </svg>
            )}
            {meter.valve_open ? "Valve Open" : "Valve Closed"}
          </span>
          <span className="text-[11px] text-gray-400">Last: {lastSeenText}</span>
        </div>

        {/* chart toggle */}
        {chartData.length > 0 && (
          <button
            onClick={() => setShowChart(!showChart)}
            className="w-full flex items-center justify-between text-[12px] font-semibold text-sky-500 hover:text-sky-600 py-1 transition-colors"
          >
            <span>{showChart ? "Hide chart" : "Show flow history"}</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className={`w-4 h-4 transition-transform ${showChart ? "rotate-180" : ""}`}>
              <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
      </div>

      {/* sparkline chart */}
      {showChart && chartData.length > 0 && (
        <div className="px-4 pb-4 border-t border-gray-50 pt-3">
          <p className="text-[11px] text-gray-400 font-medium mb-2">Flow Rate (L/min) — last {chartData.length} readings</p>
          <ResponsiveContainer width="100%" height={90}>
            <AreaChart data={chartData} margin={{ top: 2, right: 4, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id={`flowGrad${meter.id}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.2}/>
                  <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <XAxis dataKey="time" hide />
              <YAxis hide />
              <Tooltip content={<MiniTooltip />} />
              <Area type="monotone" dataKey="flow" stroke="#0ea5e9" strokeWidth={1.5} fill={`url(#flowGrad${meter.id})`} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   PAGE
═══════════════════════════════════════════════════════════ */
export default function MetersPage() {
  const [meters, setMeters] = useState<Meter[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "online" | "offline" | "fault">("all");

  useEffect(() => {
    api.get("/meters/")
      .then(({ data }) => setMeters(data.results ?? data))
      .finally(() => setLoading(false));
  }, []);

  const online  = meters.filter((m) => m.status === "online").length;
  const offline = meters.filter((m) => m.status === "offline").length;
  const fault   = meters.filter((m) => m.status === "fault").length;
  const valveOpen = meters.filter((m) => m.valve_open).length;

  const filtered = meters.filter((m) => {
    const matchSearch = !search ||
      m.serial_number.toLowerCase().includes(search.toLowerCase()) ||
      m.property_name.toLowerCase().includes(search.toLowerCase()) ||
      m.unit_number.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === "all" || m.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const STATUS_TABS: { key: "all" | "online" | "offline" | "fault"; label: string; count: number }[] = [
    { key: "all",     label: "All",     count: meters.length },
    { key: "online",  label: "Online",  count: online },
    { key: "offline", label: "Offline", count: offline },
    { key: "fault",   label: "Fault",   count: fault },
  ];

  return (
    <div className="space-y-7">

      {/* ── header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-bold text-gray-800 leading-tight">Water Meters</h1>
          <p className="text-[13px] text-gray-400 mt-0.5">
            {online} online · {offline} offline · {fault} fault
          </p>
        </div>
        <div className="flex items-center gap-2">
          {online > 0 && (
            <span className="flex items-center gap-1.5 text-[12px] text-emerald-600 bg-emerald-50 border border-emerald-100 px-3 py-1.5 rounded-xl font-semibold">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              {online} live
            </span>
          )}
        </div>
      </div>

      {/* ── KPI strip ── */}
      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Online", value: online.toString(),  sub: "Transmitting data",    color: "bg-emerald-50", stroke: "#10b981", path: <circle cx="12" cy="12" r="9"/> },
            { label: "Offline", value: offline.toString(), sub: "No recent signal",    color: "bg-gray-100",   stroke: "#6b7280", path: <circle cx="12" cy="12" r="9"/> },
            { label: "Fault",  value: fault.toString(),   sub: "Needs attention",     color: "bg-red-50",     stroke: "#ef4444", path: <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/> },
            { label: "Valves Open", value: valveOpen.toString(), sub: "Water flowing", color: "bg-sky-50",     stroke: "#0ea5e9", path: <path d="M12 2C8 7 5 10.5 5 14a7 7 0 0 0 14 0c0-3.5-3-7-7-12z" strokeLinecap="round" strokeLinejoin="round"/> },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-start gap-4">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${s.color}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke={s.stroke} strokeWidth={1.8} className="w-5 h-5">{s.path}</svg>
              </div>
              <div>
                <p className="text-[11.5px] text-gray-400 font-medium uppercase tracking-widest">{s.label}</p>
                <p className="text-[26px] font-bold text-gray-800 leading-tight mt-0.5">{s.value}</p>
                <p className="text-[11.5px] text-gray-400 mt-0.5">{s.sub}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── filters ── */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* search */}
        <div className="relative flex-1 max-w-sm">
          <svg viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth={2} className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none">
            <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Serial, property, unit…"
            className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-[13px] focus:outline-none focus:ring-2 focus:ring-sky-300 bg-white shadow-sm"
          />
        </div>
        {/* status filter */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setStatusFilter(t.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-all ${statusFilter === t.key ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
            >
              {t.label}
              <span className={`text-[10.5px] px-1.5 py-0.5 rounded-md font-semibold ${statusFilter === t.key ? "bg-gray-100 text-gray-600" : "bg-gray-200 text-gray-500"}`}>
                {t.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* ── grid ── */}
      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-64" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-14 text-center">
          <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-gray-50 flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth={1.5} className="w-7 h-7">
              <circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <p className="font-semibold text-gray-700 text-[14px]">
            {search || statusFilter !== "all" ? "No meters match your filter" : "No meters registered yet"}
          </p>
          <p className="text-[12.5px] text-gray-400 mt-1">
            {search || statusFilter !== "all" ? "Try adjusting your search." : "Connect your IoT devices to get started."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map((m) => (
            <MeterCard key={m.id} meter={m} />
          ))}
        </div>
      )}

    </div>
  );
}
